import { computed, type ComputedRef, type Ref } from 'vue';
import { createSharedComposable, useNow } from '@vueuse/core';

import {
    lastClosedTradingDay,
    latestTradingDay,
    marketSessionAt,
    type MarketSession,
    type MarketSessionState,
} from '@tmi/utils/market-clock';

/** How often the shared clock ticks. Sessions change on the minute, so 30s is at most half a minute late. */
export const MARKET_CLOCK_INTERVAL_MS: number = 30_000;

export interface MarketClock {
    now: Ref< Date >;
    state: ComputedRef< MarketSessionState >;
    session: ComputedRef< MarketSession >;
    /** Today in New York, 'yyyy-MM-dd' — the market's "today", not the viewer's. */
    marketDay: ComputedRef< string >;
    isTradingDay: ComputedRef< boolean >;
    isEarlyClose: ComputedRef< boolean >;
    /** marketDay when it is a trading day, else the trading day before it. */
    tradingDay: ComputedRef< string >;
    /** The latest trading day whose regular session has closed. */
    lastClosedTradingDay: ComputedRef< string >;
}

/**
 * The app's one ticking market clock. Every caller shares a single useNow, so there is one timer however many
 * components ask, and it stops once the last of them unmounts. A computed of DateTime.now() never recomputes, so
 * "today" built that way froze at mount and was read on the viewer's clock instead of New York's.
 *
 * The string and boolean refs only notify their dependents when the value changes (a new market day, a session
 * boundary), not on every tick.
 */
export const useMarketClock = createSharedComposable( (): MarketClock => {
    const now: Ref< Date > = useNow( { interval: MARKET_CLOCK_INTERVAL_MS } );

    const state: ComputedRef< MarketSessionState > = computed( () => marketSessionAt( now.value ) );

    return {
        now,
        state,
        session: computed( () => state.value.session ),
        marketDay: computed( () => state.value.marketDay ),
        isTradingDay: computed( () => state.value.isTradingDay ),
        isEarlyClose: computed( () => state.value.isEarlyClose ),
        tradingDay: computed( () => latestTradingDay( now.value ) ),
        lastClosedTradingDay: computed( () => lastClosedTradingDay( now.value ) ),
    };
} );
